import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TContentBlockBase } from '@onesource/schemas';
import { getMediaUrl } from '@onesource/services';
import type { FC, ReactNode } from 'react';

import { blockResourceMeta } from './types';

interface ResourceCardProps {
  block: TContentBlockBase;
  onClick: () => void;
  /** Caption under the tile — each block type's own title rule. */
  caption: string;
  /** Stored media path for the tile image; the resource icon shows when absent. */
  bannerUrl?: string | null;
  /** Rendered under the caption (the form card's draft badge). */
  children?: ReactNode;
}

/**
 * The tile every block in the lesson's document section renders as. One shell
 * for all of them, so the block types can't drift apart, and the icon always
 * comes from `blockResourceMeta` — the resource type the admin tagged, else the
 * block type's default. Shared by every lesson template via LessonDetailView.
 */
export const ResourceCard: FC<ResourceCardProps> = ({ block, onClick, caption, bannerUrl, children }) => {
  const { icon: Icon } = blockResourceMeta(block);
  const bannerSrc = bannerUrl ? getMediaUrl(bannerUrl) : '';

  return (
    <Box
      onClick={onClick}
      sx={{
        width: 107,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.25,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { opacity: 0.8, transform: 'translateY(-2px)' },
      }}
    >
      <Box sx={{ width: 53, height: 65, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {bannerSrc ? (
          <Box
            component="img"
            src={bannerSrc}
            alt={caption}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Icon size={48} color="#d32f2f" />
        )}
      </Box>
      <Typography variant="caption" sx={{ color: '#808080', fontSize: '12px', textAlign: 'center', lineHeight: 1.3 }}>
        {caption}
      </Typography>
      {children}
    </Box>
  );
};
